import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Stack,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  Smartphone as SmartphoneIcon,
  Android as AndroidIcon,
  Apple as AppleIcon,
  DesktopWindows as DesktopIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchQRCodeStats, generateQRImageUrl } from '../qrCodeApi';

const QRCodeStats = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadStats();
  }, [id]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const data = await fetchQRCodeStats(id, startDate || null, endDate || null);
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyDateFilter = () => {
    loadStats();
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'android':
        return <AndroidIcon />;
      case 'ios':
        return <AppleIcon />;
      case 'other':
        return <DesktopIcon />;
      default:
        return <SmartphoneIcon />;
    }
  };

  const getDeviceColor = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'android':
        return '#3DDC84';
      case 'ios':
        return '#000000';
      default:
        return '#757575';
    }
  };

  if (loading || !stats) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  const deviceData = Object.entries(stats.deviceStats || {});
  const totalDeviceScans = deviceData.reduce((sum, [, count]) => sum + count, 0);

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" spacing={1}>
              <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h5" fontWeight="bold">
                {t('qrCodeStatistics') || 'QR Code Statistics'}
              </Typography>
            </Stack>
          }
          action={
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/admin/qrcodes')}
              sx={{ borderRadius: 50 }}
            >
              {t('back')}
            </Button>
          }
          sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
        />
        
        <CardContent>
          {/* QR Code Info */}
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
                <img
                  src={generateQRImageUrl(stats.qrCode.TrackingUrl || `https://yourdomain.com/track?ref=${stats.qrCode.Code}`)}
                  alt={`QR-${stats.qrCode.Code}`}
                  style={{ width: '100%', maxWidth: 150, height: 'auto', borderRadius: 8 }}
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontFamily: 'monospace' }}>
                  {stats.qrCode.Code}
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Chip 
                    label={`${t('scans')}: ${stats.scanCount}`} 
                    color="info" 
                    sx={{ fontWeight: 'bold' }}
                  />
                  <Chip 
                    label={`${t('downloads')}: ${stats.downloadCount}`} 
                    color="success" 
                    sx={{ fontWeight: 'bold' }}
                  />
                  {stats.qrCode.UserId && (
                    <Chip 
                      label={`${t('assignedTo')}: User #${stats.qrCode.UserId}`} 
                      color="primary" 
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {/* Date Filter */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <TextField
                label={t('startDate') || 'Start Date'}
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label={t('endDate') || 'End Date'}
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <Button
                variant="contained"
                onClick={handleApplyDateFilter}
                sx={{ borderRadius: 50, minWidth: 120 }}
              >
                {t('applyFilter') || 'Apply'}
              </Button>
            </Stack>
          </Paper>

          {/* Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, bgcolor: 'primary.main', color: 'white', textAlign: 'center' }}>
                <Typography variant="h3" fontWeight="bold">
                  {stats.totalScans}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('totalScans') || 'Total Scans'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, bgcolor: 'success.main', color: 'white', textAlign: 'center' }}>
                <Typography variant="h3" fontWeight="bold">
                  {stats.downloadCount}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('downloads')}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, bgcolor: 'info.main', color: 'white', textAlign: 'center' }}>
                <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1.2rem' }}>
                  {stats.lastScannedAt ? new Date(stats.lastScannedAt).toLocaleDateString() : '-'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('lastScanned') || 'Last Scanned'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Device Statistics */}
          <Grid container spacing={3}>
            {/* Device Types */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  {t('deviceTypes') || 'Device Types'}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {deviceData.length > 0 ? (
                    deviceData.map(([deviceType, count]) => {
                      const percentage = totalDeviceScans > 0 ? (count / totalDeviceScans) * 100 : 0;
                      return (
                        <Box key={deviceType} sx={{ mb: 2 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              {getDeviceIcon(deviceType)}
                              <Typography variant="body2" fontWeight="500">
                                {deviceType}
                              </Typography>
                            </Stack>
                            <Typography variant="body2" fontWeight="bold">
                              {count} ({percentage.toFixed(1)}%)
                            </Typography>
                          </Stack>
                          <LinearProgress 
                            variant="determinate" 
                            value={percentage} 
                            sx={{ 
                              height: 8, 
                              borderRadius: 4,
                              bgcolor: 'grey.200',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: getDeviceColor(deviceType)
                              }
                            }}
                          />
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
                      {t('noDataAvailable') || 'No data available'}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Daily Scans */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  {t('dailyScans') || 'Daily Scans'}
                </Typography>
                <Box sx={{ mt: 2, maxHeight: 300, overflow: 'auto' }}>
                  {stats.dailyStats && stats.dailyStats.length > 0 ? (
                    stats.dailyStats.slice(-10).reverse().map((day) => {
                      const maxScans = Math.max(...stats.dailyStats.map(d => d.count));
                      const percentage = maxScans > 0 ? (day.count / maxScans) * 100 : 0;
                      return (
                        <Box key={day.date} sx={{ mb: 2 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="body2" fontWeight="500">
                              {new Date(day.date).toLocaleDateString()}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="primary.main">
                              {day.count}
                            </Typography>
                          </Stack>
                          <LinearProgress 
                            variant="determinate" 
                            value={percentage} 
                            sx={{ 
                              height: 8, 
                              borderRadius: 4,
                              bgcolor: 'grey.200'
                            }}
                          />
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
                      {t('noDataAvailable') || 'No data available'}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Recent Scans Table */}
          <Paper sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ p: 2, borderBottom: 1, borderColor: 'divider', fontWeight: 'bold' }}>
              {t('recentScans') || 'Recent Scans'}
            </Typography>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'grey.100' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('date')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('deviceType') || 'Device'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('location')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.recentScans && stats.recentScans.length > 0 ? (
                    stats.recentScans.map((scan, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(scan.ScannedAt).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {new Date(scan.ScannedAt).toLocaleTimeString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getDeviceIcon(scan.DeviceType)}
                            label={scan.DeviceType || 'Unknown'}
                            size="small"
                            variant="outlined"
                            sx={{ 
                              borderColor: getDeviceColor(scan.DeviceType),
                              color: getDeviceColor(scan.DeviceType)
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {scan.City && scan.Country ? (
                            <Typography variant="body2">
                              {scan.City}, {scan.Country}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="textSecondary">-</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                        <Typography color="textSecondary">
                          {t('noScansYet') || 'No scans yet'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </CardContent>
      </Card>
    </Box>
  );
};

export default QRCodeStats;
